import { useState, type KeyboardEvent } from 'react'
import { GripVertical, MoreVertical, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { KanbanColumn } from '@/components/kanban'
import type { KanbanColumnData } from '@/components/kanban'
import { TaskCard } from './TaskCard'
import type {
  ProjectBoardColumnId,
  ProjectBoardDocument,
  ProjectTaskDocument,
} from '@/types'

type TaskWithId = ProjectTaskDocument & { id: string }

interface Props {
  column: KanbanColumnData<TaskWithId>
  board: ProjectBoardDocument & { id: string }
  onOpenTask: (taskId: string) => void
  onCreateTask: (columnId: ProjectBoardColumnId) => void
  onRenameColumn: (columnId: string, newLabel: string) => void
  onDeleteColumn: (columnId: string) => void
}

export function BoardColumn({
  column,
  board,
  onOpenTask,
  onCreateTask,
  onRenameColumn,
  onDeleteColumn,
}: Props) {
  const [isEditing, setIsEditing] = useState(false)
  const [draftLabel, setDraftLabel] = useState(column.title)

  function commitRename() {
    const trimmed = draftLabel.trim()
    if (trimmed && trimmed !== column.title) {
      onRenameColumn(column.id, trimmed)
    } else {
      setDraftLabel(column.title)
    }
    setIsEditing(false)
  }

  function cancelRename() {
    setDraftLabel(column.title)
    setIsEditing(false)
  }

  function handleKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault()
      commitRename()
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancelRename()
    }
  }

  return (
    <KanbanColumn column={column} sortable>
      {({ dragHandleProps, droppableRef, isOver }) => (
        <div
          ref={dragHandleProps.ref}
          style={dragHandleProps.style}
          className="flex w-72 shrink-0 flex-col rounded-lg border bg-muted/30"
        >
          <div className="flex items-center gap-1 border-b px-2 py-2">
            <button
              type="button"
              className="cursor-grab touch-none rounded p-1 text-muted-foreground hover:bg-muted active:cursor-grabbing"
              aria-label="Drag column"
              {...(dragHandleProps.listeners ?? {})}
              {...dragHandleProps.attributes}
            >
              <GripVertical className="h-4 w-4" />
            </button>

            {isEditing ? (
              <Input
                autoFocus
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                onBlur={commitRename}
                onKeyDown={handleKey}
                className="h-7 flex-1 px-2 py-1 text-sm"
              />
            ) : (
              <h3
                className="flex-1 cursor-text truncate rounded px-1 text-sm font-semibold hover:bg-muted/60"
                onClick={() => {
                  setDraftLabel(column.title)
                  setIsEditing(true)
                }}
                title="Click to rename"
              >
                {column.title}
              </h3>
            )}

            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {column.cards.length}
            </span>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  aria-label="Column options"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-44 p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="w-full justify-start text-destructive hover:bg-destructive/10 hover:text-destructive"
                  onClick={() => {
                    if (window.confirm(`Delete column "${column.title}"?`)) {
                      onDeleteColumn(column.id)
                    }
                  }}
                >
                  Delete column
                </Button>
              </PopoverContent>
            </Popover>
          </div>

          <div
            ref={droppableRef}
            className={`flex flex-1 flex-col gap-2 p-2 transition-colors ${
              isOver ? 'bg-accent/30' : ''
            }`}
          >
            {column.cards.map((task) => (
              <TaskCard key={task.id} task={task} board={board} onOpen={onOpenTask} />
            ))}
            {column.cards.length === 0 && (
              <div className="rounded-md border border-dashed py-6 text-center text-xs text-muted-foreground">
                Drop tasks here
              </div>
            )}
          </div>

          <div className="border-t p-2">
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-muted-foreground"
              onClick={() => onCreateTask(column.id)}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add task
            </Button>
          </div>
        </div>
      )}
    </KanbanColumn>
  )
}
